import React, { useState, useEffect, useCallback } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { CivicAuthProvider } from '@civic/auth-web3/react';
import { WalletProvider } from './context/WalletContext.jsx';
import { NetworkProvider } from './context/NetworkContext';
import InstallButton from './components/InstallButton';
import UpdateNotification from './components/UpdateNotification';
import NetworkStatusIndicator from './components/NetworkStatusIndicator';
import ServiceWorkerUpdateDialog from './components/ServiceWorkerUpdateDialog';
import OfflinePage from './pages/OfflinePage';
import useNetworkStatus from './hooks/useNetworkStatus';
import NetworkStatus from './components/NetworkStatus';
import DashboardNavbar from './components/DashboardNavbar';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import Pools from './components/Pools';
import Send from './components/Send';
import SendWallet from './components/SendWallet';
import SendBank from './components/SendBank';
import ProtectedPools from './components/ProtectedPools';
import RegularPool from './components/RegularPool';
import TransactionsPage from './pages/TransactionsPage';
import BuyCrypto from './components/BuyCrypto';
import InvestPage from './components/InvestPage';
import BondDetail from './components/BondDetail';
import StockDetail from './pages/StockDetail';
import UserInvestmentPortfolio from './pages/UserInvestmentPortfolio';

import { ConnectivityProvider } from './context/ConnectivityContext';
import './styles.css';

// Layout component that includes the DashboardNavbar
const AuthenticatedLayout = () => (
  <>
    <DashboardNavbar />
    <main className="dashboard-main">
      <Outlet />
    </main>
  </>
);

// Wrapper component to handle online/offline state
const AppContent = () => {
  const { isOnline, isConnectionSuitable } = useNetworkStatus();
  const location = useLocation();
  const [showOfflinePage, setShowOfflinePage] = useState(!isOnline);

  useEffect(() => {
    // Only show offline page if we're completely offline, not just on a slow connection
    setShowOfflinePage(!isOnline);
    
    // If connection is too slow for the current page, you could implement
    // a more graceful degradation here
    if (!isConnectionSuitable({ minSpeed: 0.5, maxLatency: 1000 })) {
      console.warn('Connection quality may affect performance');
    }
  }, [isOnline, location, isConnectionSuitable]);

  // Show offline page if offline and not already on the offline page
  if (showOfflinePage && !location.pathname.startsWith('/offline')) {
    return <Navigate to="/offline" state={{ from: location }} replace />;
  }

  return (
    <>
      <InstallButton />
      <NetworkStatus />
      <Routes>
        <Route path="/offline" element={<OfflinePage />} />
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/*"
          element={
            <WalletProvider>
              <AuthenticatedLayout />
            </WalletProvider>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="pools" element={<Pools />} />
          <Route path="invest" element={<InvestPage />} />
          <Route path="bonds" element={<InvestPage />} />
          <Route path="bonds/:bondAddress" element={<BondDetail />} />
          <Route path="stocks" element={<InvestPage />} />
          <Route path="stocks/:tokenAddress" element={<StockDetail />} />
          <Route path="send" element={<Send />} />
          <Route path="send/wallet" element={<SendWallet />} />
          <Route path="transfer" element={<SendBank />} />
          <Route path="lulo" element={<ProtectedPools />} />
          <Route path="regular" element={<RegularPool />} />
          <Route path="transactions" element={<TransactionsPage />} />
          <Route path="buy-crypto" element={<BuyCrypto />} />
          <Route path="investment-portfolio" element={<UserInvestmentPortfolio />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </>
  );
};

function App() {
  return (
    <Router>
      <ConnectivityProvider>
        <NetworkProvider>
          <WalletProvider>
            <CivicAuthProvider clientId="d1be3da3-4156-4c07-9a73-a81a64478192">
              <ServiceWorkerUpdateDialog />
              <AppContent />
              <Analytics />
            </CivicAuthProvider>
          </WalletProvider>
        </NetworkProvider>
      </ConnectivityProvider>
    </Router>
  );
}

export default App;